import { ReactNode, ReactElement } from 'react';

import AccessDenied from './AccessDenied';

export type RouteGuardProps = {
    children: ReactNode;
    /**
     * Whether the user has permission to access this route
     */
    hasPermission: boolean;
    /**
     * Whether the permission check is still loading
     */
    isLoading?: boolean;
    /**
     * Component to show when access is denied (defaults to AccessDenied component)
     */
    accessDeniedComponent?: ReactNode;
};

/**
 * Generic route guard component that conditionally renders children based on permissions.
 * Prevents flash of content by returning null while loading.
 */
const RouteGuard = ({
    children,
    hasPermission,
    isLoading = false,
    accessDeniedComponent = <AccessDenied />,
}: RouteGuardProps): ReactElement | null => {
    // Wait for permission check to complete before rendering anything
    // This prevents the flash of restricted content
    if (isLoading) {
        return null;
    }

    // If permission is denied, show the access denied component
    if (!hasPermission) {
        return (accessDeniedComponent ?? <AccessDenied />) as ReactElement;
    }

    // Otherwise, render the children (the actual route component)
    return children as ReactElement;
};

export default RouteGuard;
